import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash';

// barcode scanner
import BarcodeScannerComponent from "react-qr-barcode-scanner";

// custom components
import { CustomModal } from '_molecules';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectEmployeeQRScannerList, sendEmployeeNotification } from "_redux/slices/timeline/employees";
import { selectSelectedTask, reset as resetSelectedTask } from "_redux/slices/timeline/selected-task";
import {
    createTask,
    selectCreateTaskStatus,
    clearCreateTaskStatus,
    selectCreateTaskError,
    selectTimelineData,
    setTimelineData,
    updateTask,
    selectTaskList,
    selectUpdateTaskStatus,
    resetUpdateTaskFlags,
    paxCheckIn,
    selectPaxCheckInStatus,
    resetPaxCheckInFlags
} from "_redux/slices/timeline/timeline-data";
import { selectFirebaseInfo } from "_redux/slices/main-screen/user";
import { selectSelectedTimeline, selectDropOffLocations, selectPickUpLocations } from "_redux/slices/timeline/timeline-areas";

// bootstrap
import Row from 'react-bootstrap/Row';
import Button from 'react-bootstrap/Button';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Form from 'react-bootstrap/Form';

// exterior libraries
import { Title as TremorTitle, Subtitle, Flex } from '@tremor/react';

// styles 
import { Spacing, Colors } from '_styles';

// icons
import { MdQrCode2 } from "react-icons/md";
import { IoCloseCircle } from "react-icons/io5";
import { CiCirclePlus, CiCircleMinus } from "react-icons/ci";


// constants
import { EmployeeScannerHours } from '_utils/timeline-constants';

// functions
import { getSeatNumber } from "_utils/functions";

const EmployeeScanner = () => {

    // nav
    const history = useHistory();

    //#region redux 
    const dispatch = useDispatch();
    const allEmployees = useSelector(selectEmployeeQRScannerList);                     // all employess without any filtering
    const selectedTask = useSelector(selectSelectedTask);
    const dbUser = useSelector(selectFirebaseInfo);
    const timelineData = useSelector(selectTimelineData);                              // timeline data object
    const selectedTimeline = useSelector(selectSelectedTimeline);
    const createTaskStatus = useSelector(selectCreateTaskStatus);
    const createTaskError = useSelector(selectCreateTaskError);
    const dropOffLocations = useSelector(selectDropOffLocations);                   // airport drop off locations
    const pickUpLocations = useSelector(selectPickUpLocations);                     // airport pick up locations
    const taskList = useSelector(selectTaskList);                                   // time Line task list
    const updateTaskStatus = useSelector(selectUpdateTaskStatus);                   // update task status 
    const paxCheckInStatus = useSelector(selectPaxCheckInStatus);                   // status of passenger check in 
    const iata = dbUser.details.authority;
    //#endregion

    //#region local states
    const [scannedEmployee, setScannedEmployee] = useState(null)
    const [assignedTime, setAssignedTime] = useState("")                             // holds value of the task assigned Time
    const [timeDifference, setTimeDifference] = useState(0);
    const [warningText, setWarningText] = useState("");
    const [selectTaskError, setSelectedTaskError] = useState(false);                 // if there is a selected task
    const [scannedEmployeeWarning, setScannedEmployeeWarning] = useState(false)      // true if the employee doesn't belong to the selected timeline
    // QR scanner
    const [hasPermission, setHasPermission] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [streamToggle, setStreamToggle] = useState(false);
    const [selectedScanner, setSelectedScanner] = useState("cameraScanner")
    const [handScannerError, setHandScannerError] = useState("")
    // modal
    const [successModal, setSuccessModal] = useState(false);
    const [errorModal, setErrorModal] = useState(false);
    const [errorBody, setErrorBody] = useState('');
    const [selectedDropOff, setSelectedDropOff] = useState(null)                    // drop off location of the PAX
    const [selectedPickUp, setSelectedPickUp] = useState(null)                      // pick up location of the PAX
    //#endregion

    //#region useEffect

    useLayoutEffect(() => {
        dispatch(clearCreateTaskStatus());
        dispatch(resetUpdateTaskFlags());
        handleReset()
    }, [])

    // Check for camera permissions
    useEffect(() => {
        navigator.permissions.query({ name: 'camera' })
            .then((permissionObj) => {
                if (permissionObj.state === 'granted') {
                    setHasPermission(true);
                } else if (permissionObj.state === 'prompt') {
                    askForCameraPermission();
                } else {
                    setErrorMessage('Camera access denied. Please enable camera permissions to use this feature.');
                }
            })
            .catch((error) => {
                console.error('Permission not found', error);
                setErrorMessage('Error checking camera permissions.');
            });
    }, []);

    useEffect(() => {
        if (Object.isObjectEmpty(selectedTask)) return;
        if (!assignedTime) return
        // Helper function to format the current date string
        const getFormattedCurrentDate = () => {
            const currentDate = new Date();
            const pad = (num) => (num < 10 ? "0" + num : num.toString());
            return `${currentDate.getFullYear()}-${pad(
                currentDate.getMonth() + 1
            )}-${pad(currentDate.getDate())}`;
        };

        // Assign time calculation
        const dateString = getFormattedCurrentDate();
        // const assignedTime = new Date(`${dateString}T${job.assignedTime}Z`);
        const formattedAssignedTime = new Date(`${dateString}T${assignedTime}`);

        let flightTime, timeType;

        // Determine flight time and type based on origin and destination
        if (selectedTask?.flightConfirmationDetails?.origin == iata) {
            flightTime = new Date(selectedTask.flightConfirmationDetails.departureTime);
            timeType = "Departure";
        } else if (selectedTask?.flightConfirmationDetails?.destination == iata) {
            flightTime = new Date(selectedTask.flightConfirmationDetails.arrivalTime);
            timeType = "Arrival";
        }

        // Calculate time difference and generate warning message if flight time is determined
        setWarningText(getTimeDifferenceWarning(formattedAssignedTime, flightTime, timeType));
        // if the selected task has isALTPickUp then it's an alternative pick up task and has a pick up location so set it's value to the local state selectPickUp
        if (selectedTask?.isALTPickUp) {
            setSelectedPickUp(selectedTask?.pickUpLocation?.id ?? null)
        };

        // if selectedTask has paxPositionInfo this task is checked in passenger 
        if (!Object.isObjectEmpty(selectedTask?.paxPositionInfo)) {
            // check if dropOffLocations is Array with length get the the element with id equal to dropOffLocation in paxPositionInfo
            if (Array.isFullArray(dropOffLocations)) {
                let tempDropOff = dropOffLocations?.find((dropoff) => dropoff?._id == selectedTask?.paxPositionInfo?.dropOffLocation);
                if (tempDropOff) {
                    setSelectedDropOff(tempDropOff?._id);
                }
            };
            // check if pickUpLocations is Array with length get the the element with id equal to pickUpLocation in paxPositionInfo
            if (Array.isFullArray(pickUpLocations)) {
                let tempPickUp = pickUpLocations?.find((pickup) => pickup?._id == selectedTask?.paxPositionInfo?.pickUpLocation);
                if (tempPickUp) {
                    setSelectedPickUp(tempPickUp?._id);
                }
            }
        }
    }, [selectedTask, assignedTime]);

    useEffect(() => {
        // if there is no selected time line task can be assigned to any employee
        if (Object.isObjectEmpty(selectedTimeline)) return
        // if the employee QR code isn't scanned yet 
        if (Object.isObjectEmpty(scannedEmployee)) return

        // if the scanned employee doesn't belong to the selected timeline
        if (selectedTimeline?._id !== scannedEmployee?.workZone?._id) setScannedEmployeeWarning(true)
    }, [scannedEmployee, selectedTimeline])

    // create Task Status listener
    useEffect(() => {
        if (createTaskStatus == "succeeded" || updateTaskStatus == "succeeded") {
            // send notification to employee if fcm token exsists
            if (scannedEmployee?.FCMTokens) {
                let notification = {
                    notificationTitle: "New Assigned Task at " + assignedTime,
                    notificationText: `Please collect passenger ${selectedTask?.name ? selectedTask?.name : ""}`,
                    fcmToken: scannedEmployee?.FCMTokens,
                    iata
                };
                dispatch(sendEmployeeNotification(notification));
            }
            setSuccessModal(true)
        }
        if (createTaskStatus == "failed") {
            setErrorModal(true);
            setErrorBody('There was an issue assigning the task. Please try again. ' + createTaskError)
        }
    }, [createTaskStatus, updateTaskStatus]);

    useEffect(() => {
        if (paxCheckInStatus == "succeeded" || paxCheckInStatus == "failed") {
            dispatch(resetPaxCheckInFlags());
        }
    }, [paxCheckInStatus]);

    //#endregion


    //#region functions

    const handleGoBack = () => {
        dispatch(resetSelectedTask())
        history.push('/timeline-view')
    }

    const handleReset = () => {
        setScannedEmployee(null)
        setAssignedTime("")
        setWarningText("")
        setSelectedTaskError(false)
        setScannedEmployeeWarning(false)
        setSuccessModal(false);
    }

    const handleComplete = () => {
        handleReset()
        dispatch(clearCreateTaskStatus());
        dispatch(resetUpdateTaskFlags());
        handleGoBack();
    }

    // Function to request camera access
    const askForCameraPermission = () => {
        navigator.mediaDevices.getUserMedia({ video: true })
            .then((stream) => {
                stream.getTracks().forEach(track => track.stop()); // stop the stream so it doesn't stay on
                setHasPermission(true);
            })
            .catch((error) => {
                console.error('Error accessing camera', error);
                setErrorMessage('Access to camera was denied.');
            });
    };

    // Helper function to calculate time difference and generate warning message
    const getTimeDifferenceWarning = (assignedTime, flightTime, timeType) => {
        const timeDifference = Math.abs(assignedTime - flightTime);
        const timeDifferenceMins = Math.floor(timeDifference / (1000 * 60));
        const timeDifferenceHours = Math.floor(timeDifferenceMins / 60);
        const laterOrEarlier = assignedTime > flightTime ? "later" : "earlier";

        let timeString = "";

        setTimeDifference(laterOrEarlier);

        if (timeDifferenceHours > 0) {
            timeString += `${timeDifferenceHours} hours `;
        }
        if (timeDifferenceMins % 60 > 0) {
            timeString += `${timeDifferenceMins % 60} minutes `;
        }

        if (!timeString)
            return `Assigned Time is The same time as ${timeType} Time.`;
        return `Assigned Time is ${timeString.trim()} ${laterOrEarlier} than ${timeType} Time.`;
    };

    // add each confirmed task to the time line cach
    const addTimelineTask = (job, column) => {
        // find destination column
        const destinationColumn = { ...timelineData.columns[column] };

        // find list of jobs in destination column
        const destinationJobs = [...destinationColumn.jobs];

        // add job to list
        destinationJobs.push(job);

        // set old job list to new list
        destinationColumn.jobs = destinationJobs;

        // create new data object for timeline data
        const newTimelineData = {
            ...timelineData,
            columns: { ...timelineData.columns, [column]: destinationColumn },
        };

        // update timeline
        dispatch(setTimelineData({ data: newTimelineData, job: job }));
    };

    // function to open the scanner again
    const resetScanner = () => {
        setScannedEmployee(null)
        setScannedEmployeeWarning(false)
    }

    const confirmTask = async () => {
        // Check if there is a selectedTask
        if (Object.isObjectEmpty(selectedTask)) return setSelectedTaskError(true);

        // create job object using selected task and column
        let job = {
            ...selectedTask,
            assignedTime,
            employeeId: scannedEmployee?.employeeId,
        };

        // add the task to the timeline
        addTimelineTask(job, job?.assignedTime, job?.employeeId);
        // if selected task has isALTPickUp it's an alternative pick up task and already exists in the tasklist so it only needs to be updated 
        if (selectedTask?.isALTPickUp) {
            let tempALTPickUp = taskList?.find((taskItem) => taskItem?._id === selectedTask?._id);

            if (tempALTPickUp) {
                let taskUpdates = {
                    id: tempALTPickUp._id,
                    assignedTime: column,
                    employeeId: employeeId,
                    pickUpLocation: formatPickUpLocation(selectedPickUp),
                    dropOffLocation: formatDropOffLocation(selectedDropOff),
                    pending: false
                }
                dispatch(updateTask(taskUpdates));
            };

        } else {
            // create object to be dispatched to backend
            let taskObject = {
                active: false,
                completed: false,
                employeeId: job.employeeId,
                assignedTime: job.assignedTime,
                flightInformation: {
                    arrivalAirport: job.flightConfirmationDetails?.destination,
                    departureAirport: job.flightConfirmationDetails?.origin,
                    flightNumber: `${job.flightConfirmationDetails?.carrierCode}${job.flightConfirmationDetails?.flightNumber}`,
                    flightTime:
                        job.flightConfirmationDetails.destination == iata
                            ? job.flightConfirmationDetails?.arrivalTime
                            : job.flightConfirmationDetails?.departureTime,
                    flightDate:
                        job.flightConfirmationDetails.destination == iata
                            ? job.flightConfirmationDetails?.arrivalTime?.split("T")[0]
                            : job.flightConfirmationDetails?.departureTime?.split("T")[0],
                    arrivalTime: job.flightConfirmationDetails?.arrivalTime,
                    departureTime: job.flightConfirmationDetails?.departureTime,

                    departureDate: job.flightConfirmationDetails?.departureDate,
                    gate: "044",
                    terminalTitle: "2",
                    seatNumber: getSeatNumber(job),
                },
                passengerInformation: {
                    assistance: job.assistance ? job.assistance : [],
                    title: job?.name ? job?.name : "n/a",
                },
                flightBooking: job.flightBooking ? job.flightBooking : false,
                allocatedTime: new Date(),
                _id: job["_id"],
                cancelled: false,
                pending: false,
                selectedTimeline: selectedTimeline?._id ?? null,
                dropOffLocation: formatDropOffLocation(selectedDropOff),
                pickUpLocation: formatPickUpLocation(selectedPickUp),
                isTransitBooking: job?.isTransitBooking ? true : false,
                notPlanned: false,
                isEstimatedBooking: job?.isEstimatedBooking ? true : false,
                passengerDutTime: job?.passengerDutTime ? job?.passengerDutTime : null,
                combinedBooking: job?.combinedBooking ? true : false,
                passengers: Array.isFullArray(job?.passengers) ? job?.passengers?.map((passenger) => {
                    return {
                        assistance: passenger?.assistance ?? [],
                        name: passenger?.name ?? "n/a"
                    }
                }) : null,
                bookingCreateDate: job?.dateCreated ?? null
            };

            // check if job has flightBooking it's arrival or if it has isEstimatedBooking it Estimated departure add originalBookingId to the task for edit booking
            if (job?.flightBooking || job?.isEstimatedBooking) taskObject["originalBookingId"] = job?.originalBookingId ?? null;
            if (job?.flightBooking) taskObject["bookingCreateDate"] = job?.originalBookingDoc?.dateCreated ?? null;
            if (job?.combinedRampBooking) taskObject["combinedRampBooking"] = true;
            if (job?.combinedBooking && !job.originalBookingId) taskObject["originalBookingId"] = job._id;

            //check if selectedTask has paxPositionInfo it's a checked in passenger booking add the checkInTime to task
            if (!Object.isObjectEmpty(selectedTask?.paxPositionInfo)) taskObject["checkInTime"] = selectedTask?.paxPositionInfo?.checkInTime
            // save task to firestore
            // dispatch(createTask(taskObject));
            const res = await dispatch(createTask(taskObject));
            // check response from createTask is successful change the paxStatus in paxPositionInfo to assigned
            if (res?.meta?.requestStatus === "fulfilled" && !Object.isObjectEmpty(selectedTask?.paxPositionInfo)) {
                let payload = {
                    // if booking has isEstimatedBooking then choose the originalBookingId else _id
                    bookingId: selectedTask?.isEstimatedBooking ? selectedTask?.originalBookingId : selectedTask?._id,
                    body: {
                        paxPositionInfo: {
                            ...selectedTask?.paxPositionInfo,
                            paxStatus: "assigned",
                            assignedTime: new Date()
                        }
                    }
                }
                dispatch(paxCheckIn(payload));
            }
        }
    };

    // cancel task
    const cancelTask = () => {
        handleComplete()
    }

    const handleScan = (result) => {
        let scannedEmployeeId = result.text;
        // if employee gets scanned prevent the scanner for keeps scanning
        if (Object.isObjectEmpty(scannedEmployee)) {
            if (scannedEmployeeId) {
                let tempScannedEmployee = allEmployees?.filter((employee) => employee.employeeId == scannedEmployeeId)?.[0]
                setScannedEmployee(tempScannedEmployee)
            }
        }
    }

    // debounce is used to take the value after onChange finish 
    const handeScannerChange = debounce((event) => {
        if (!event.target.value) {
            console.error("No barcode data provided");
            setHandScannerError("No barcode data provided")
            return null;
        }
        handleScan({ text: event.target.value })
    }, 500); // Adjust the delay as needed

    // format drop off location to result object with location id and location name 
    const formatDropOffLocation = (id) => {
        if (!id) return null;
        const location = dropOffLocations.find((location) => location._id === id);
        if (location) return { id: location._id, name: location.title };
        else return null
    }

    // format pick up location to result object with location id and location name 
    const formatPickUpLocation = (id) => {
        if (!id) return null;
        const location = pickUpLocations.find((location) => location._id === id);
        if (location) return { id: location._id, name: location.title };
        else return null
    }

    //#endregion

    return (
        <div style={{ justifyContent: 'center', display: 'flex' }}>

            {/* show scanned employee and task */}
            {!Object.isObjectEmpty(scannedEmployee) &&
                <div className='mt-3' style={{ width: 800, height: 800, borderRadius: 25 }}>

                    {/* spacer */}
                    <div className='mt-5' />
                    <TremorTitle>Confirm Task</TremorTitle>
                    <Subtitle>Review the information of the Task, employee and chose a assign time to the task before confirming it</Subtitle>

                    {/* no selected taskwarning message */}
                    {selectTaskError && (
                        <div style={{ backgroundColor: Colors.LIGHT_RED, borderRadius: 10, borderLeftWidth: 3, borderLeftStyle: "solid", borderLeftColor: Colors.DARK_RED }}>
                            <div style={{ padding: Spacing.SCALE_18, display: "flex", alignItems: "center" }} >
                                <CiCircleMinus size={Spacing.SCALE_42} color={Colors.DARK_RED} />
                                <p style={{ color: Colors.DARK_RED, display: "flex", margin: 0, marginLeft: Spacing.SCALE_12 }} >
                                    Please go back to time line and select a task from the Task List
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Flight + Employee information */}
                    <div style={{ marginLeft: Spacing.SCALE_8, marginTop: Spacing.SCALE_22, }} >
                        <div style={{ display: "flex" }}>
                            <div>
                                <Subtitle>Flight</Subtitle>
                                <p>{`${selectedTask?.flightConfirmationDetails?.carrierCode}${selectedTask?.flightConfirmationDetails?.flightNumber}`}</p>
                            </div>
                            <div style={{ marginLeft: Spacing.SCALE_22 }}>
                                <Subtitle>Passenger</Subtitle>
                                <p>{selectedTask?.name ? selectedTask?.name : ""}</p>
                            </div>
                            <div style={{ marginLeft: Spacing.SCALE_22 }}>
                                <Subtitle>Employee</Subtitle>
                                <p>
                                    {scannedEmployee?.title ?? ""}
                                </p>
                            </div>
                            <div style={{ marginLeft: Spacing.SCALE_22 }}>
                                <Subtitle>Employee Email</Subtitle>
                                <p>
                                    {scannedEmployee?.email ?? ""}
                                </p>
                            </div>
                        </div>

                        {/* scanned employee + selected timeline warning message */}
                        {scannedEmployeeWarning && (
                            <div>
                                <div style={{ backgroundColor: Colors.LIGHT_RED, borderRadius: 10, borderLeftWidth: 3, borderLeftStyle: "solid", borderLeftColor: Colors.DARK_RED }}>
                                    <div style={{ padding: Spacing.SCALE_18, display: "flex", alignItems: "center" }} >
                                        <CiCircleMinus size={Spacing.SCALE_42} color={Colors.DARK_RED} />
                                        <p style={{ color: Colors.DARK_RED, display: "flex", margin: 0, marginLeft: Spacing.SCALE_12 }} >
                                            The scanned employee doesn't belong to the selected timeline please select an employee belongs to the selected timeline
                                        </p>
                                    </div>

                                </div>
                                <Button variant='outline' style={{ marginTop: "20px", borderColor: Colors.PRM_BLUE, color: Colors.PRM_BLUE }} onClick={resetScanner}>{'Open Scanner'}</Button>
                            </div>
                        )}

                        {/* select Assigned time, drop off, pick up locations  */}
                        {!scannedEmployeeWarning && <div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>


                                <div><Subtitle>Assigned Time</Subtitle>
                                    <select value={assignedTime} onChange={(e) => { setAssignedTime(e.target.value) }} style={{ height: Spacing.SCALE_50, minWidth: "25%", marginTop: Spacing.SCALE_10 }}>
                                        <option value={""}>Choose Assign time...</option>
                                        {EmployeeScannerHours?.map((hour, index) => <option key={index} value={hour}>{hour}</option>)}
                                    </select >
                                </div>

                                <div>
                                    <Subtitle>Pick Up Location</Subtitle>
                                    <select value={selectedPickUp} onChange={(e) => { setSelectedPickUp(e.target.value) }} style={{ height: Spacing.SCALE_50, minWidth: "25%", marginTop: Spacing.SCALE_10, borderRadius: "5px" }}>
                                        <option value={""}>Choose Pick Up Location...</option>
                                        {pickUpLocations?.map((pickUpLocation, index) => <option key={index} value={pickUpLocation?._id}>{pickUpLocation?.title}</option>)}
                                    </select >
                                </div>
                                <div>
                                    <Subtitle>Drop Off Location</Subtitle>
                                    <select value={selectedDropOff} onChange={(e) => { setSelectedDropOff(e.target.value) }} style={{ height: Spacing.SCALE_50, minWidth: "25%", marginTop: Spacing.SCALE_10, borderRadius: "5px" }}>
                                        <option value={""}>Chose Drop Off Location...</option>
                                        {dropOffLocations?.map((dropOffLocation, index) => <option key={index} value={dropOffLocation?._id}>{dropOffLocation?.title}</option>)}
                                    </select >
                                </div>

                            </div>


                            {/* Assigned time warning message */}
                            {assignedTime && <div
                                style={{
                                    backgroundColor: timeDifference === "earlier" ? Colors.LIGHT_GREEN : Colors.LIGHT_RED,
                                    borderRadius: 10,
                                    borderLeftWidth: 3,
                                    borderLeftStyle: "solid",
                                    borderLeftColor: timeDifference === "earlier" ? Colors.DARK_GREEN : Colors.DARK_RED,
                                    marginTop: Spacing.SCALE_20
                                }}
                            >
                                <div style={{ padding: Spacing.SCALE_18, display: "flex", alignItems: "center" }} >
                                    {timeDifference !== "earlier" ? (
                                        <CiCircleMinus size={Spacing.SCALE_42} color={timeDifference === "earlier" ? Colors.DARK_GREEN : Colors.DARK_RED} />
                                    ) : (
                                        <CiCirclePlus size={24} color={timeDifference === "earlier" ? Colors.DARK_GREEN : Colors.DARK_RED} />
                                    )}
                                    <p style={{ color: timeDifference === "earlier" ? Colors.DARK_GREEN : Colors.DARK_RED, display: "flex", alignItems: "center", margin: 0, marginLeft: Spacing.SCALE_12 }} >
                                        {warningText}
                                    </p>
                                </div>
                            </div>
                            }
                        </div>}
                    </div>
                    {assignedTime && <Row className='mt-5'>
                        <Flex>
                            <Button variant='outline-danger' onClick={cancelTask}>{'Cancel Task'}</Button>
                            <Button variant='outline-success' onClick={confirmTask}>{'Confirm Task'}</Button>
                        </Flex>
                    </Row>
                    }
                </div>
            }

            {/* show QR scanner */}
            {Object.isObjectEmpty(scannedEmployee) &&
                <div style={{ width: "100%", display: "grid", justifyContent: "center", alignItems: "center" }}>
                    <Tabs
                        id="controlled-tab-example"
                        activeKey={selectedScanner}
                        onSelect={(k) => setSelectedScanner(k)}
                        className="mb-3"
                        fill
                        justify
                    >
                        <Tab eventKey="cameraScanner" title="Camera Scanner" >
                            {hasPermission ? <div className='mt-3' style={{ width: 800, height: 800, borderRadius: 25 }}>
                                {/* spacer */}
                                <div className='mt-5' />
                                {/* text */}
                                <div style={{ display: "flex", justifyContent: "center" }}>
                                    <div style={{ borderRadius: 10, backgroundColor: Colors.PRM_LIGHT_BLUE, width: "fit-content", height: 80, display: 'flex', justifyContent: 'center', alignItems: "center", }}>
                                        <MdQrCode2
                                            style={{ marginLeft: 12, marginTop: 8 }}
                                            color={"white"}
                                            size={60}
                                        />
                                        <h3 style={{ color: 'white', margin: "0 25px" }}>Scan Employee QR Code</h3>
                                        <IoCloseCircle onClick={handleGoBack} style={{ cursor: 'pointer', marginRight: 12, marginTop: 8 }} color={'white'} size={32} />
                                    </div>
                                </div>
                                {/* blue outline */}
                                <div style={{ borderRadius: 10, borderWidth: 5, borderStyle: 'solid', borderColor: Colors.PRM_LIGHT_BLUE, zIndex: 1, opacity: 1.0, width: 400, height: 350, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                                {/* camera */}
                                <div className='mt-2' style={{ borderRadius: 10, borderWidth: 2.5, borderStyle: 'solid', borderColor: 'black', }}>
                                    <BarcodeScannerComponent
                                        width={'100%'}
                                        height={'100%'}
                                        onUpdate={(err, result) => { if (result) { handleScan(result) }; }}
                                        stopStream={streamToggle}
                                    />
                                </div>
                            </div> : <div className='mt-3' style={{ width: 800, height: 800, borderRadius: 25 }}>
                                <TremorTitle>Camera Permission Required</TremorTitle>
                                <Subtitle>In order to scan employee QR code PRM Assist requires permission to use your devices camera, please click 'Allow' to enable the camera and scan boarding passes</Subtitle>
                                <Button variant='outline-primary' className='mt-3' onClick={askForCameraPermission}>Request Camera Permission</Button>
                            </div>}

                        </Tab>
                        <Tab eventKey="handScanner" title="Hand Scanner" >
                            <div className='mt-3' style={{ width: 800, height: 800, borderRadius: 25 }}>

                                <Form.Group className="mb-3" controlId="exampleForm.ControlTextarea1">
                                    <Form.Label>Please Click in the field below then use the scanner</Form.Label>
                                    <Form.Control as="textarea" rows={6} onChange={handeScannerChange} />
                                </Form.Group>
                                <p className='text-danger mt-2' style={{ color: Colors.TEXT_COLOR }}>{handScannerError}</p>
                            </div>
                        </Tab>
                    </Tabs>
                </div>


            }

            {/* success modal */}
            <CustomModal
                hide={handleComplete}
                show={successModal}
                title={'Success'}
                body={'The task has been successfully assigned'}
                button={'Done'}
                openButton={handleComplete}
            />

            {/* error modal */}
            <CustomModal
                hide={handleComplete}
                show={errorModal}
                title={'Error'}
                body={errorBody}
                button={'Done'}
                openButton={handleComplete}
            />

        </div>
    );
};

export default EmployeeScanner;
